"use client"

import type React from "react"
import { type FormEvent, useEffect, useState } from "react"
import { observer } from "mobx-react-lite"
import { useAppContext } from "../../../../shared/functions/Context"
import { hideModalFromId } from "../../../../shared/functions/ModalShow"
import MODAL_NAMES from "../../../dialogs/ModalName"
import { type ITemplates, defaultTemplate } from "../../../../shared/models/three-sixty-feedback-models/Templates"

const EditTemplateModal = observer(() => {
  const { api, store } = useAppContext()
  const [template, setTemplate] = useState<ITemplates>({ ...defaultTemplate })
  const [loading, setLoading] = useState(false)
  const [questionText, setQuestionText] = useState("")
  const [statements, setStatements] = useState<
    {
      statement: string
      id: string
      rating: number
      editing: boolean
    }[]
  >([])

  // Add a new statement to the list
  const addStatement = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    if (questionText.trim() !== "") {
      const newStatement = {
        id: new Date().toISOString(),
        statement: questionText,
        rating: 0,
        editing: false,
      }
      setStatements([...statements, newStatement])
      setQuestionText("")
    }
  }

  // Save template changes
  const handleSaveTemplate = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setLoading(true)

    const updatedTemplate = {
      ...template,
      statements: statements.map((statement) => ({
        ...statement,
        editing: false, // Turn off editing mode when saving
      })),
    }

    if (template.title.trim() !== "" && statements.length > 0) {
      try {
        await api.templates.update(updatedTemplate)
        setLoading(false)
        onCancel() // Reset states and hide modal
      } catch (error) {
        setLoading(false)
        console.error("Error updating template:", error)
        // Handle error appropriately, e.g., show error message to user
      }
    } else {
      setLoading(false)
      alert("Please add a title and at least one statement to the template.")
    }
  }

  // Effect to initialize modal with selected template (if any)
  useEffect(() => {
    if (store.templates.selected) {
      setTemplate({ ...store.templates.selected })
      setStatements(
        store.templates.selected.statements.map((statement) => ({
          ...statement,
          editing: false, // Initialize editing mode as false for existing statements
        })),
      )
    } else {
      setTemplate({ ...defaultTemplate })
      setStatements([])
      hideModalFromId(MODAL_NAMES.THREE_SIXTY_FEEDBACK.EDIT_TEMPLATE_MODAL)
    }
  }, [store.templates, store.templates.selected])

  // Cancel changes and reset modal state
  const onCancel = () => {
    setTemplate({ ...defaultTemplate })
    setStatements([])
    hideModalFromId(MODAL_NAMES.THREE_SIXTY_FEEDBACK.EDIT_TEMPLATE_MODAL)
  }

  // Handle statement text change
  const handleStatementChange = (index: number, value: string) => {
    const updatedStatements = [...statements]
    updatedStatements[index].statement = value
    setStatements(updatedStatements)
  }

  // Toggle edit mode for a statement
  const toggleEditMode = (index: number) => {
    const updatedStatements = [...statements]
    updatedStatements[index].editing = !updatedStatements[index].editing
    setStatements(updatedStatements)
  }

  // Delete a statement from the list
  const deleteStatement = (index: number) => {
    const updatedStatements = [...statements]
    updatedStatements.splice(index, 1) // Remove the statement at index
    setStatements(updatedStatements)
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-full bg-purple-100 text-purple-600">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 3H5c-1.103 0-2 .897-2 2v14c0 1.103.897 2 2 2h14c1.103 0 2-.897 2-2V5c0-1.103-.897-2-2-2zM5 19V5h14l.001 14H5z"></path>
                <path d="M7 7h10v2H7zm0 4h10v2H7zm0 4h7v2H7z"></path>
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900">Edit Template</h3>
          </div>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-600 transition-colors">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M9.172 16.242 12 13.414l2.828 2.828 1.414-1.414L13.414 12l2.828-2.828-1.414-1.414L12 10.586 9.172 7.758 7.758 9.172 10.586 12l-2.828 2.828z"></path>
              <path d="M12 22c5.514 0 10-4.486 10-10S17.514 2 12 2 2 6.486 2 12s4.486 10 10 10zm0-18c4.411 0 8 3.589 8 8s-3.589 8-8 8-8-3.589-8-8 3.589-8 8-8z"></path>
            </svg>
          </button>
        </div>

        <form onSubmit={handleSaveTemplate} className="space-y-4">
          <div className="space-y-2">
            <label htmlFor="template-title" className="text-sm font-medium text-gray-700">
              Template Title
            </label>
            <input
              id="template-title"
              type="text"
              placeholder="Enter template title"
              value={template.title}
              onChange={(e) => setTemplate({ ...template, title: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>

          <div className="space-y-3">
            <label className="text-sm font-medium text-gray-700">Statements</label>
            <ul className="space-y-2">
              {statements.map((statement, index) => (
                <li key={statement.id} className="flex items-center gap-2">
                  {statement.editing ? (
                    <input
                      type="text"
                      value={statement.statement}
                      onChange={(e) => handleStatementChange(index, e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  ) : (
                    <>
                      <span className="flex-1 text-gray-700">
                        {index + 1}. {statement.statement}
                      </span>
                      <button
                        type="button"
                        onClick={() => toggleEditMode(index)}
                        className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                        >
                          <path d="M4 21a1 1 0 0 0 .24 0l4-1a1 1 0 0 0 .47-.26L21 7.41a2 2 0 0 0 0-2.82L19.42 3a2 2 0 0 0-2.83 0L4.3 15.29a1.06 1.06 0 0 0-.27.47l-1 4A1 1 0 0 0 3.76 21 1 1 0 0 0 4 21zM18 4.41 19.59 6 18 7.59 16.42 6zM5.91 16.51 15 7.41 16.59 9l-9.1 9.1-2.11.52z"></path>
                        </svg>
                      </button>
                      <button
                        type="button"
                        onClick={() => deleteStatement(index)}
                        className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                        >
                          <path d="M5 20a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8h2V6h-4V4a2 2 0 0 0-2-2H9a2 2 0 0 0-2 2v2H3v2h2zM9 4h6v2H9zM8 8h9v12H7V8z"></path>
                          <path d="M9 10h2v8H9zm4 0h2v8h-2z"></path>
                        </svg>
                      </button>
                    </>
                  )}
                </li>
              ))}
            </ul>

            <div className="space-y-2">
              <input
                type="text"
                placeholder="Enter new statement"
                value={questionText}
                onChange={(e) => setQuestionText(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <button
                type="button"
                onClick={addStatement}
                disabled={!questionText.trim()}
                className="flex items-center justify-center gap-2 w-full px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="12" y1="5" x2="12" y2="19"></line>
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
                Add Statement
              </button>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onCancel}
              disabled={loading}
              className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !template.title.trim() || statements.length === 0}
              className="px-4 py-2 text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  Saving...
                </>
              ) : (
                "Save"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
})

export default EditTemplateModal
